<template>
  <div>
    <!-- In Impostazioni compare solo il riquadro di lancio: l'editor vero vive in un dialog a tutto schermo -->
    <v-card outlined>
      <v-card-title class="d-flex align-center">
        <v-icon left class="mr-2">mdi-robot-outline</v-icon>
        {{ t('admConfig.aiActions.title') }}
        <v-chip v-if="hasChanges" color="warning" size="small" label class="ml-3">
          {{ t('admConfig.statusEditor.unsaved') }}
        </v-chip>
      </v-card-title>
      <v-card-text>
        <div class="text-body-2 text-medium-emphasis mb-4">
          {{ t('admConfig.aiActions.description') }}
        </div>
        <div class="d-flex align-center flex-wrap" style="gap: 12px;">
          <v-btn color="primary" prepend-icon="mdi-robot-outline" @click="openEditor">
            {{ t('admConfig.aiActions.openEditor') }}
          </v-btn>
          <span class="text-caption text-medium-emphasis">
            {{ t('admConfig.aiActions.actionCount', { count: workingActions.length }) }}
          </span>
        </div>
      </v-card-text>
    </v-card>

    <!-- Editor a tutto schermo -->
    <v-dialog v-model="editorDialog" fullscreen :scrim="false" transition="dialog-bottom-transition">
      <v-card class="d-flex flex-column">
        <v-toolbar density="compact" color="primary" dark class="flex-shrink-0">
          <v-btn icon @click="requestClose">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ t('admConfig.aiActions.title') }}</v-toolbar-title>
          <v-chip v-if="hasChanges" color="warning" size="small" label class="ml-3">
            {{ t('admConfig.statusEditor.unsaved') }}
          </v-chip>
          <v-spacer />
          <v-btn variant="text" prepend-icon="mdi-undo" :disabled="!hasChanges || saving" @click="resetChanges">
            {{ t('admConfig.statusEditor.discard') }}
          </v-btn>
          <v-btn
            variant="flat"
            color="white"
            class="text-primary ml-2"
            prepend-icon="mdi-content-save"
            :loading="saving"
            :disabled="!hasChanges"
            @click="save"
          >
            {{ t('admConfig.aiActions.save') }}
          </v-btn>
        </v-toolbar>

        <v-card-text class="flex-grow-1 overflow-y-auto">
          <v-alert v-if="saveError" type="error" variant="tonal" density="compact" class="mb-3">
            {{ saveError }}
            <ul v-if="saveErrorDetails.length" class="mt-2 ml-4">
              <li v-for="(detail, i) in saveErrorDetails" :key="i">{{ detail }}</li>
            </ul>
          </v-alert>
          <v-alert v-if="saveSuccess" type="success" variant="tonal" density="compact" class="mb-3">
            {{ saveSuccess }}
          </v-alert>

          <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px;">
            <v-btn color="primary" variant="tonal" prepend-icon="mdi-plus" @click="createAction">
              {{ t('admConfig.aiActions.add') }}
            </v-btn>
            <v-text-field
              v-model="filter"
              :label="t('admConfig.aiActions.filter')"
              prepend-inner-icon="mdi-magnify"
              density="compact"
              clearable
              hide-details
              style="max-width: 320px;"
            />
            <span class="text-caption text-medium-emphasis">
              {{ t('admConfig.aiActions.hint') }}
            </span>
          </div>

          <v-alert v-if="!workingActions.length" type="info" variant="tonal" density="compact">
            {{ t('admConfig.aiActions.empty') }}
          </v-alert>

          <v-card
            v-for="action in filteredActions"
            :key="action.id"
            variant="outlined"
            class="mb-2"
            :class="{ 'action--disabled': action.enabled === false }"
          >
            <v-card-text class="py-3">
              <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                <v-icon>{{ action.icon || 'mdi-robot-outline' }}</v-icon>
                <span class="text-subtitle-2">{{ action.label }}</span>
                <code class="text-caption">{{ action.id }}</code>

                <v-chip size="x-small" label variant="tonal">
                  {{ action.engine === 'tool'
                    ? t('admConfig.statusEditor.verifications.engineTool')
                    : t('admConfig.statusEditor.verifications.engineBackend') }}
                </v-chip>
                <v-chip v-if="action.engine === 'tool'" size="x-small" label variant="tonal" color="teal">
                  <v-icon start size="x-small">mdi-api</v-icon>
                  {{ action.tool }}.{{ action.call }}
                </v-chip>
                <!-- Nome proprio del documento: distingue due azioni sulla stessa chiamata -->
                <v-chip
                  v-if="action.file?.nameTemplate"
                  size="x-small" label variant="tonal" color="indigo"
                  :title="action.file.nameTemplate"
                >
                  <v-icon start size="x-small">mdi-file-outline</v-icon>
                  {{ action.file.nameTemplate }}
                </v-chip>
                <v-chip v-else-if="action.steps?.length" size="x-small" label variant="tonal" color="primary">
                  {{ t('admConfig.aiActions.stepCount', { count: action.steps.length }) }}
                </v-chip>
                <v-chip v-else-if="action.skill" size="x-small" label variant="tonal">
                  {{ action.skill }}
                </v-chip>
                <!-- Riservata a certi profili: si vede dall'elenco, senza aprire l'azione -->
                <v-chip
                  v-for="roleId in (action.roles || [])"
                  :key="`role-${roleId}`"
                  size="x-small" label variant="tonal" color="deep-purple"
                  prepend-icon="mdi-account-lock-outline"
                >
                  {{ roleLabelOf(roleId) }}
                </v-chip>
                <v-chip v-if="action.validationField" size="x-small" label variant="tonal" color="success">
                  <v-icon start size="x-small">mdi-check-decagram-outline</v-icon>
                  {{ action.validationField }}
                </v-chip>
                <!-- Prerequisiti: si vedono dall'elenco, senza aprire l'azione -->
                <v-chip
                  v-if="conditionsLabel(action)"
                  size="x-small" label variant="tonal" color="orange"
                  :title="conditionsLabel(action)"
                >
                  <v-icon start size="x-small">mdi-lock-outline</v-icon>
                  {{ conditionsLabel(action) }}
                </v-chip>
                <v-chip v-if="action.enabled === false" size="x-small" label variant="tonal" color="warning">
                  {{ t('admConfig.statusEditor.verifications.disabled') }}
                </v-chip>
                <v-chip v-if="missingSkillsFor(action).length" size="x-small" label variant="tonal" color="error">
                  <v-icon start size="x-small">mdi-alert-outline</v-icon>
                  {{ t('admConfig.aiActions.missingSkill', { skills: missingSkillsFor(action).join(', ') }) }}
                </v-chip>

                <v-spacer />

                <v-btn icon variant="text" size="small" :title="t('admConfig.aiActions.edit')" @click="editAction(action)">
                  <v-icon size="small">mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon variant="text" size="small" :title="t('admConfig.aiActions.duplicate')" @click="duplicateAction(action)">
                  <v-icon size="small">mdi-content-copy</v-icon>
                </v-btn>
                <v-btn icon variant="text" size="small" color="error" :title="t('admConfig.aiActions.remove')" @click="askRemove(action)">
                  <v-icon size="small">mdi-delete</v-icon>
                </v-btn>
              </div>

              <!-- Chi la richiama: la stessa azione può stare in più stati e in più tipi di processo -->
              <div class="text-caption text-medium-emphasis mt-2">
                <template v-if="usageFor(action.id).length">
                  {{ t('admConfig.aiActions.usedBy', { statuses: usageFor(action.id).join(', ') }) }}
                </template>
                <template v-else>
                  {{ t('admConfig.aiActions.unused') }}
                </template>
              </div>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>

    <AiActionEditDialog
      v-model="editDialog"
      :action="editingAction"
      :is-new="editingIsNew"
      :existing-ids="workingActions.map(a => a.id)"
      :skills="skills"
      :fields="allFields"
      :roles="workflowRoles"
      :tools="toolsCatalog"
      :tools-config="projectStore.toolsConfig"
      :used-by="editingAction ? usageFor(editingAction.id) : []"
      @commit="onActionCommit"
    />

    <!-- Eliminare un'azione ancora richiamata lascerebbe gli stati con un riferimento rotto -->
    <v-dialog v-model="removeDialog" max-width="560">
      <v-card v-if="removingAction">
        <v-card-title>{{ t('admConfig.aiActions.removeTitle') }}</v-card-title>
        <v-card-text>
          <p>{{ t('admConfig.aiActions.removeText', { label: removingAction.label, id: removingAction.id }) }}</p>
          <v-alert
            v-if="usageFor(removingAction.id).length"
            type="warning"
            variant="tonal"
            density="compact"
            class="mt-3"
          >
            {{ t('admConfig.aiActions.removeBlocked', { statuses: usageFor(removingAction.id).join(', ') }) }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="removeDialog = false">{{ t('admConfig.statusEditor.cancel') }}</v-btn>
          <v-btn
            variant="flat"
            color="error"
            :disabled="usageFor(removingAction.id).length > 0"
            @click="confirmRemove"
          >
            {{ t('admConfig.aiActions.remove') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Chiusura con modifiche non salvate -->
    <v-dialog v-model="confirmCloseDialog" max-width="480" persistent>
      <v-card>
        <v-card-title>{{ t('admConfig.statusEditor.confirmCloseTitle') }}</v-card-title>
        <v-card-text>{{ t('admConfig.aiActions.confirmCloseText') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="confirmCloseDialog = false">{{ t('admConfig.statusEditor.cancel') }}</v-btn>
          <v-btn variant="flat" color="error" @click="discardAndClose">{{ t('admConfig.statusEditor.discard') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useProjectStore } from '../stores/projects.js'
import AiActionEditDialog from './AiActionEditDialog.vue'
import { describeCondition, normalizeConditions } from '../utils/fieldRules.js'

const { t } = useI18n()
const projectStore = useProjectStore()

// Biblioteca dei profili (cfg.json -> roles): si edita in "Profili e fasi", qui si legge soltanto
const workflowRoles = computed(() => projectStore.workflowRoles || [])
const roleLabelOf = (id) => workflowRoles.value.find(r => r.id === id)?.label || id

// Copia di lavoro: nulla tocca cfg.json finché non si preme Salva
const workingActions = ref([])
const originalJson = ref('')
const skills = ref([])
// Catalogo dei tool (dal codice), per le azioni engine "tool"
const toolsCatalog = ref([])
const filter = ref('')

const editorDialog = ref(false)
const editDialog = ref(false)
const editingAction = ref(null)
const editingIsNew = ref(false)
const removeDialog = ref(false)
const removingAction = ref(null)
const confirmCloseDialog = ref(false)

const saving = ref(false)
const saveError = ref('')
const saveErrorDetails = ref([])
const saveSuccess = ref('')

const hasChanges = computed(() => JSON.stringify(workingActions.value) !== originalJson.value)

const allFields = computed(() => {
  const byType = projectStore.fieldsByProjectType || {}
  return Object.values(byType).flat()
})

const filteredActions = computed(() => {
  const term = (filter.value || '').trim().toLowerCase()
  if (!term) return workingActions.value
  return workingActions.value.filter(a =>
    (a.id || '').toLowerCase().includes(term)
    || (a.label || '').toLowerCase().includes(term)
    || (a.skill || '').toLowerCase().includes(term)
    || (a.steps || []).some(s => (s.skill || '').toLowerCase().includes(term))
  )
})

// Stati che richiamano un'azione, letti dalla configurazione salvata sul server: la modifica di
// un'azione non cambia chi la usa, quindi non serve la copia di lavoro dell'editor stati.
const usageFor = (actionId) => {
  const out = []
  ;(projectStore.projectTypes || []).forEach(type => {
    ;(type.statusList || []).forEach(status => {
      const used = (status.verifications || []).some(v => v && v.ref === actionId)
      if (used) out.push(`${type.descr} / ${status.descr}`)
    })
  })
  return out
}

// Skill richiamate dall'azione che non hanno un file .md nella cartella del motore effettivo
// Riassunto dei prerequisiti dell'azione per il chip dell'elenco. Vuoto = nessuna condizione.
const conditionsLabel = (action) => {
  const conditions = normalizeConditions(action?.conditionalOn)
  if (!conditions.length) return ''
  const words = {
    yes: t('admConfig.conditions.yes'),
    no: t('admConfig.conditions.no'),
    filled: t('admConfig.conditions.filled'),
    empty: t('admConfig.conditions.emptyValue')
  }
  return conditions.map(c => describeCondition(c, fieldLabelOf(c.field), words)).join(' · ')
}

const fieldLabelOf = (name) => allFields.value.find(f => f.name === name)?.label || name

const missingSkillsFor = (action) => {
  if (!skills.value.length || action.engine === 'tool') return []
  const wanted = (action.steps || []).length
    ? action.steps.map(s => s.skill)
    : [action.skill]
  return wanted.filter(skill => skill && !skills.value.some(s => s.id === skill))
}

const loadFromStore = () => {
  const raw = projectStore.aiActions
  workingActions.value = Array.isArray(raw) ? JSON.parse(JSON.stringify(raw)) : []
  originalJson.value = JSON.stringify(workingActions.value)
}

const resetChanges = () => {
  loadFromStore()
  saveError.value = ''
  saveErrorDetails.value = []
  saveSuccess.value = ''
}

const openEditor = async () => {
  // Riallinea alla configurazione sul server, ma senza buttare via un lavoro in corso
  if (!hasChanges.value) {
    await projectStore.loadConfigurations()
    loadFromStore()
  }
  // Le skill invecchiano in fretta: si rileggono a ogni apertura
  try {
    skills.value = await projectStore.loadSkills()
  } catch (e) {
    console.error('Errore nel caricamento delle skill:', e)
  }
  try {
    toolsCatalog.value = (await projectStore.loadToolsConfig())?.catalog || []
  } catch (e) {
    console.error('Errore nel caricamento del catalogo dei tool:', e)
  }
  editorDialog.value = true
}

const requestClose = () => {
  if (hasChanges.value) {
    confirmCloseDialog.value = true
    return
  }
  editorDialog.value = false
}

const discardAndClose = () => {
  confirmCloseDialog.value = false
  resetChanges()
  editorDialog.value = false
}

const createAction = () => {
  editingAction.value = null
  editingIsNew.value = true
  editDialog.value = true
}

const editAction = (action) => {
  editingAction.value = JSON.parse(JSON.stringify(action))
  editingIsNew.value = false
  editDialog.value = true
}

// Copia con un id nuovo: è il modo veloce di partire da un'azione che funziona
const duplicateAction = (action) => {
  const copy = JSON.parse(JSON.stringify(action))
  let candidate = `${copy.id}Copia`
  let n = 2
  while (workingActions.value.some(a => a.id === candidate)) candidate = `${copy.id}Copia${n++}`
  copy.id = candidate
  editingAction.value = copy
  editingIsNew.value = true
  editDialog.value = true
}

const onActionCommit = (action) => {
  const index = workingActions.value.findIndex(a => a.id === action.id)
  if (index >= 0) workingActions.value.splice(index, 1, action)
  else workingActions.value.push(action)
  saveSuccess.value = ''
}

const askRemove = (action) => {
  removingAction.value = action
  removeDialog.value = true
}

const confirmRemove = () => {
  workingActions.value = workingActions.value.filter(a => a.id !== removingAction.value.id)
  removeDialog.value = false
  removingAction.value = null
}

const save = async () => {
  saving.value = true
  saveError.value = ''
  saveErrorDetails.value = []
  saveSuccess.value = ''
  try {
    // Solo la biblioteca: i tipi di processo restano quelli dell'ultimo salvataggio dell'editor stati
    const result = await projectStore.saveCfgParts({ aiActions: workingActions.value })
    loadFromStore()
    saveSuccess.value = t('admConfig.aiActions.saved', {
      count: workingActions.value.length,
      backup: result?.backup || '—'
    })
  } catch (e) {
    saveError.value = e?.message || t('admConfig.statusEditor.saveError')
    const details = e?.response?.data?.errors
    if (Array.isArray(details)) saveErrorDetails.value = details
  } finally {
    saving.value = false
  }
}

loadFromStore()
</script>

<style scoped>
.action--disabled {
  opacity: 0.6;
}
</style>
